import type { CaseFormValues } from "@/components/Forms/CaseForm.tsx";
import type { CaseFilterEditing } from "@/queries/useFetchCaseFiltersets.ts";
import { useFetchEmployeeByType } from "@/queries/useFetchEmployeeByType.ts";
import { CircularProgress, MenuItem, Paper, type SxProps, Typography } from "@mui/material";
import { RhfTextField } from "mui-rhf-integration";
import { useFormContext } from "react-hook-form";

type Props = {
    name: keyof CaseFormValues | keyof CaseFilterEditing;
    label: string;
    type: string;
    includeInactive?: boolean;
    disabled?: boolean;
    sx?: SxProps;
};

const EmployeeByType = ({
    name,
    label,
    type,
    sx,
    includeInactive = false,
    disabled = false,
}: Props) => {
    const form = useFormContext();
    const { data, isError, isRefetching, isLoading, error } = useFetchEmployeeByType({
        type,
        includeInactive,
    });

    if (isError) {
        return (
            <Paper
                sx={{
                    p: 2,
                    borderRadius: "8px",
                }}
            >
                <Typography variant={"h5"}>Error loading employees</Typography>
                <Typography variant={"body1"}>{error.message}</Typography>
            </Paper>
        );
    }

    if (isLoading || isRefetching) {
        return (
            <Paper
                sx={{
                    p: 2,
                    borderRadius: "8px",
                }}
            >
                <CircularProgress size={"12px"} />
            </Paper>
        );
    }

    return (
        <RhfTextField
            select
            control={form.control}
            name={name}
            label={label}
            disabled={disabled}
            sx={{
                width: "100%",
                backgroundColor: "#fff",
                ...sx,
            }}
        >
            {data?.map((caseStatus) => (
                <MenuItem key={`${name}-${caseStatus.idUUID}`} value={`${caseStatus.idUUID}`}>
                    {caseStatus.fullName}
                </MenuItem>
            ))}
        </RhfTextField>
    );
};

export default EmployeeByType;
